import React, { useMemo } from "react";
import { MdArrowDropUp, MdArrowDropDown } from "react-icons/md";
import Card from "components/card";
import LineChart from "components/charts/LineChart";
import { useRelatorios } from "context/RelatorioContext";
import { useDevices } from "context/DeviceContext";

interface Props {
  selectedUnidadeId: string | "Todas";
}

export default function RelatorioPessoas({ selectedUnidadeId }: Props) {
  const { relatorios } = useRelatorios();
  const { devices } = useDevices();

  const relatoriosFiltrados = relatorios.filter(r =>
    selectedUnidadeId === "Todas" || r.dispositivoId === selectedUnidadeId
  );

  const hoje = new Date();
  const mesAtual = hoje.getMonth();
  const anoAtual = hoje.getFullYear();

  const totalMesAtual = relatoriosFiltrados
    .filter(r => {
      const dataRelatorio = new Date(r.Data + "T12:00:00");
      return dataRelatorio.getMonth() === mesAtual && dataRelatorio.getFullYear() === anoAtual;
    })
    .reduce((sum, r) => sum + r.contador, 0);

  const dataMesAnterior = new Date(anoAtual, mesAtual - 1, 1);
  const mesAnterior = dataMesAnterior.getMonth();
  const anoAnterior = dataMesAnterior.getFullYear();

  const totalMesAnterior = relatoriosFiltrados
    .filter(r => {
      const dataRelatorio = new Date(r.Data + "T12:00:00");
      return dataRelatorio.getMonth() === mesAnterior && dataRelatorio.getFullYear() === anoAnterior;
    })
    .reduce((sum, r) => sum + r.contador, 0);

  let variacaoPercentual = 0;
  if (totalMesAnterior > 0) {
    variacaoPercentual = ((totalMesAtual - totalMesAnterior) / totalMesAnterior) * 100;
  } else if (totalMesAtual > 0) {
    variacaoPercentual = 100;
  }
  const variacaoPositiva = variacaoPercentual >= 0;

  const { lineChartData, lineChartOptions } = useMemo(() => {
    const todosOsExpedientes = Array.from(
      new Set(devices.flatMap(d => Object.keys(d.expedientes || {})))
    ).sort();

    const dadosPorExpediente: Record<string, number[]> = {};
    todosOsExpedientes.forEach(exp => {
      dadosPorExpediente[exp] = [];
    });

    const meses = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
    const labelsMeses: string[] = [];

    for (let i = 9; i >= 0; i--) {
      const dataIteracao = new Date(anoAtual, mesAtual - i, 1);
      const mesIteracao = dataIteracao.getMonth();
      const anoIteracao = dataIteracao.getFullYear();

      labelsMeses.push(meses[mesIteracao]);

      const relatoriosDoMes = relatoriosFiltrados.filter(r => {
        const dataRelatorio = new Date(r.Data + "T12:00:00");
        return dataRelatorio.getMonth() === mesIteracao && dataRelatorio.getFullYear() === anoIteracao;
      });

      todosOsExpedientes.forEach(expediente => {
        const soma = relatoriosDoMes
          .filter(r => r.expediente === expediente)
          .reduce((sum, r) => sum + r.contador, 0);
        dadosPorExpediente[expediente].push(soma);
      });
    }

    const cores = ["#CF4949", "#B84343", "#A33D3D", "#8E3737", "#793131"];

    const finalLineChartData = todosOsExpedientes.map((expediente, index) => ({
      name: expediente,
      data: dadosPorExpediente[expediente],
      color: cores[index % cores.length],
    }));

    const finalLineChartOptions = {
      legend: { show: true },
      theme: { mode: "light" },
      chart: { type: "line", toolbar: { show: false } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth" },
      tooltip: { style: { fontSize: "12px" }, theme: "light" },
      grid: { show: false },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: "#A3AED0", fontSize: "12px", fontWeight: "500" } },
        type: "text",
        categories: labelsMeses,
      },
      yaxis: { show: false },
    };

    return { lineChartData: finalLineChartData, lineChartOptions: finalLineChartOptions };
  }, [relatoriosFiltrados, devices, anoAtual, mesAtual]);


  return (
    <Card extra="!p-[20px] text-center">
      <h2 className="text-lg font-bold text-navy-700 self-start">Total do Mês</h2>
      <div className="flex h-full w-full flex-row justify-between sm:flex-wrap lg:flex-nowrap 2xl:overflow-hidden">
        <div className="flex flex-col">
          <p className="mt-[20px] text-3xl font-bold text-navy-700">
            {totalMesAtual} <span className="text-base font-medium">Pessoas</span>
          </p>
          <div className="flex flex-col items-start">
            <div className="flex flex-row items-center justify-center">
              {variacaoPositiva ? (
                <MdArrowDropUp className="font-medium text-green-500" />
              ) : (
                <MdArrowDropDown className="font-medium text-red-500" />
              )}
              <p className={`text-sm font-bold ${variacaoPositiva ? 'text-green-500' : 'text-red-500'}`}>
                {variacaoPositiva ? '+' : ''}{variacaoPercentual.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
        <div className="h-full w-full">
          <LineChart chartOptions={lineChartOptions} chartData={lineChartData} />
        </div>
      </div>
    </Card>
  );
}
